// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useStoresList from "@/hooks/useStoresList";
import useLang from "@/hooks/useLang";
import useStore from "@/hooks/useStore";
import type { TyActiveStore } from "@/hooks/useStore/types";
import useTranslation from "@/hooks/useTranslation";
import type { TyStoreMembership } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyStoreItem = TyStoreMembership;

type Props = {
    onClose: () => void;
};

// --- Helpers ------------------------------------------------------------------------------------

function getStoreName(store: TyActiveStore, lang: string | null): string {
    const match = store.name.find((n) => n.lang === lang) ?? store.name[0];
    return match?.value ?? "—";
}

// --- Logic --------------------------------------------------------------------------------------

export default function SwitchStorePopup({ onClose }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const navigate = useNavigate();
    const activeStoreId = useStore((state) => state.storeId);
    const setStore = useStore((state) => state.setStore);

    const { stores, loading, error } = useStoresList();

    const handleSelect = (store: TyStoreItem) => {
        onClose();
        if (store._id === activeStoreId) return;
        setStore(store._id, store);
        navigate("/");
    };

    const handleCreateStore = () => {
        onClose();
        navigate("/create-store");
    };

    // --- Render ---------------------------------------------------------------------------------

    return (
        <div className="switch-store-popup">
            {loading && (
                <div className="ssp-loading">
                    <span className="ssp-spinner" />
                </div>
            )}
            {!loading && error && <p className="ssp-error">{error}</p>}
            {!loading && !error && (
                <div className="ssp-list">
                    {stores.map((store) => {
                        const name = getStoreName(store, lang);
                        const isActive = store._id === activeStoreId;
                        return (
                            <button
                                key={store._id}
                                type="button"
                                className={`ssp-item${isActive ? " ssp-item--active" : ""}`}
                                onClick={() => handleSelect(store)}
                            >
                                <div className="ssp-logo">
                                    {store.logo ? <img src={store.logo} alt={name} /> : <span className="ssp-logo-fallback">{name.charAt(0)}</span>}
                                </div>
                                <p className="ssp-name">{name}</p>
                                {isActive && <span className="ssp-active-dot" />}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="ssp-divider" />

            <button type="button" className="ssp-create" onClick={handleCreateStore}>
                <img src="/icons/plus-gray-icon.svg" alt="" width={12} height={12} />
                <span>{t.switchStorePopup.createStore}</span>
            </button>
        </div>
    );
}
